import sharesproApi from 'rtkQuery/sharespro/sharesproApiService';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import mapHomeDataFromApi from 'rtkQuery/sharespro/mapper/modules/home/mapHomeDataFromApi';
import { OtpId } from 'model/modules/otp/types';
import mapPortfolioChartDataFromApi from 'rtkQuery/sharespro/mapper/modules/portfolio/mapPortfolioChartDataFromApi';
import { HomePageData } from 'model/modules/home/homePageData';
import { Rebalance } from 'model/modules/portfolio/rebalance';
import usePeriodQueryParam from 'hooks/modules/period/usePeriodQueryParam';
import { mapRebalanceFromApi } from 'rtkQuery/sharespro/mapper/modules/portfolio/mapPortfolioDataFromApi';

export const homeApi = sharesproApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchHomeChart: builder.query<
      PortfolioChartData,
      ApiFetchPortfolioQueryParams
    >({
      query: (params) => ({
        url: '/v3/finance/dashboard/chart',
        method: 'GET',
        params,
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiFetchPortfolioChartResponseData>
      ) {
        return mapPortfolioChartDataFromApi(baseQueryReturnValue.data);
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const resp = await queryFulfilled;
        dispatch(
          homeApi.util.updateQueryData('fetchHome', data, (draft) => {
            draft.chart = resp.data;
          })
        );
      },
    }),
    fetchHome: builder.query<HomePageData, ApiFetchHomeQueryParams>({
      query: (params) => ({
        url: '/v3/finance/main',
        method: 'GET',
        params,
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiFetchHomeResponseData>
      ) {
        return mapHomeDataFromApi(baseQueryReturnValue);
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return { account_id: queryArgs.account_id };
      },
    }),
    fetchRebalancingDetails: builder.query<Rebalance, number>({
      query: (rebalanceId) => ({
        url: '/v3/finance/rebalancing/detail',
        query: {
          id: rebalanceId,
        },
        method: 'GET',
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiRebalancing>
      ) {
        return mapRebalanceFromApi(baseQueryReturnValue.data);
      },
    }),
    requestRebalancingConfirmation: builder.mutation<OtpId, ID>({
      query: (id) => ({
        url: '/v3/finance/rebalancing/confirm',
        method: 'POST',
        sendToken: true,
        params: { id },
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiRequestOtpResponse>
      ) {
        return baseQueryReturnValue.data.otp_id;
      },
    }),
    verifyRebalanceConfirmation: builder.mutation<
      Rebalance,
      ApiVerifyRebalancingConfirmationRequest
    >({
      query: ({ otpId, ...data }) => ({
        url: '/v3/finance/rebalancing/confirm-code',
        method: 'POST',
        sendToken: true,
        params: { id: otpId },
        body: data,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiRebalancing>
      ) {
        return mapRebalanceFromApi(baseQueryReturnValue.data);
      },
      async onQueryStarted(data, { queryFulfilled }) {
        try {
          await queryFulfilled;
          window.location.reload();
        } catch {
        }
      },
    }),
    updateAccount: builder.mutation<boolean, ApiUpdateBrokerAccount>({
      query: (data) => ({
        url: `/v3/finance/account/info?account_id=${data.account_id.toString()}`,
        method: 'PATCH',
        sendToken: true,
        body: {
          name: data.name,
          description: data.description,
        },
      }),
      transformResponse(baseQueryReturnValue: boolean) {
        return baseQueryReturnValue;
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        dispatch(
          homeApi.util.updateQueryData(
            'fetchHome',
            {
              period: usePeriodQueryParam,
            },
            (draft) => {
              const account = draft.accounts.items.find(
                (acc) => acc.id === data.account_id
              );
              if (account) {
                account.name = data.name ?? account.name;
              }

              return draft;
            }
          )
        );
      },
    }),
  }),
});

export const {
  useFetchHomeQuery,
  useFetchRebalancingDetailsQuery,
  useRequestRebalancingConfirmationMutation,
  useVerifyRebalanceConfirmationMutation,
  useLazyFetchHomeChartQuery,
  useUpdateAccountMutation,
} = homeApi;
